/**
 * Which accidentals get printed, worked out once per part before any bar is written.
 *
 * The document stores what a note *sounds* -- a step, an octave and a number of semitones
 * -- and leaves the printing of accidentals to whoever engraves it, which is what
 * `accidental: "auto"` means. That is the right thing to store: an F sharp is an F sharp
 * whether the sharp is drawn in front of it, carried from the signature, or already in
 * force from earlier in the bar.
 *
 * It is not enough to hand to a renderer. MusicXML lets a note state `<alter>` with no
 * `<accidental>` and leave the decision to the reader, but readers do not agree on it, and
 * the one this package renders through does not print naturals at all: an F natural after
 * an F sharp in the same bar, written that way, is drawn as a second F sharp, and read back
 * as one. The note is then wrong on the page and wrong in the file, from a document that
 * was right. A part in a key of its own makes it worse, because the accidental a note needs
 * depends on the signature that part prints rather than the one the piece is in.
 *
 * So the decision is made here, by the rules a copyist uses: an accidental is printed when
 * a note disagrees with what is already in force for its own step and octave -- the
 * signature at the start of the bar, then whatever accidental has been printed since. The
 * state is per staff and per bar, as the convention is, and it is shared by the voices on
 * that staff, which is why the bar's notes are walked in time order rather than voice by
 * voice. A note tied from the one before prints nothing: it is not struck again.
 */

import type { EventId } from "../document/ids";
import { partKey } from "../document/key";
import { keyAlter } from "../document/pitch";
import type { NotePitch } from "../document/pitch";
import { eventOnsets } from "../document/query";
import type { KeySignature, Part, PartMeasure, Score } from "../document/types";

/**
 * The accidental each head of an event needs, `null` where it needs none.
 *
 * Decided for every head, including the ones whose display the document states outright:
 * the export uses the decision only where the document left it open, and the import uses
 * it the other way round, to tell a file's ordinary accidentals from its courtesy ones.
 */
export type AccidentalPlan = ReadonlyMap<EventId, readonly (string | null)[]>;

const NAMES: Readonly<Record<number, string>> = {
  [-2]: "flat-flat",
  [-1]: "flat",
  0: "natural",
  1: "sharp",
  2: "double-sharp",
};

/** MusicXML's name for an alteration, or `null` for one it has no name for. */
export function accidentalName(alter: number): string | null {
  return NAMES[alter] ?? null;
}

/** One note head of a bar, with everything needed to place it in the bar's order. */
interface Head {
  readonly at: number;
  readonly voice: number;
  readonly staff: number;
  readonly eventId: EventId;
  readonly index: number;
  readonly count: number;
  readonly pitch: NotePitch;
}

/**
 * Every head of a bar in the order a player meets them.
 *
 * Voices run at the same time, so the order is by onset first: an accidental in the lower
 * voice is in force for the upper one from that beat onwards, which is what a reader does
 * with it. Ties are left in the order the voices are listed, which keeps a grace note in
 * front of the note it leans on, both of them standing at the same tick.
 */
function headsOf(measure: PartMeasure): readonly Head[] {
  const heads: Head[] = [];
  measure.voices.forEach((voice, number) => {
    const onsets = eventOnsets(voice);
    voice.events.forEach((event, index) => {
      if (event.kind !== "note") return;
      event.pitches.forEach((pitch, at) => {
        heads.push({
          at: onsets[index] ?? 0,
          voice: number,
          staff: voice.staff,
          eventId: event.id,
          index: at,
          count: event.pitches.length,
          pitch,
        });
      });
    });
  });
  return heads.sort((a, b) => a.at - b.at || a.voice - b.voice);
}

/** Work out every accidental a part prints, bar by bar. */
export function accidentalPlan(score: Score, part: Part): AccidentalPlan {
  const plan = new Map<EventId, (string | null)[]>();
  let instrument = part.instrument;
  let sounding: KeySignature | null = null;
  let fifths = 0;

  for (const global of score.measures) {
    const measure = part.measures[global.id];
    // The signature a part reads against moves with the instrument in the player's hands
    // as much as with the piece's key: the same written notes under a piccolo and under a
    // clarinet are read against signatures a tone apart, so both are followed.
    const taken = measure?.instrument ?? null;
    if (taken !== null) instrument = { ...part.instrument, ...taken };
    if (global.key !== null) sounding = global.key;
    if (sounding !== null) fifths = partKey(sounding, instrument).fifths;
    if (measure === undefined) continue;

    // A bar wipes the slate: what an accidental put in force lasts to the barline and no
    // further, so the signature is all that is left standing at the next one.
    const inForce = new Map<string, number>();

    for (const head of headsOf(measure)) {
      const { pitch } = head;
      const row = plan.get(head.eventId) ?? new Array<string | null>(head.count).fill(null);
      plan.set(head.eventId, row);
      // A drum head sits on a staff line rather than at a pitch: nothing alters it, and
      // nothing it does alters anything after it.
      if (pitch.drum !== null) continue;

      const slot = `${head.staff}:${pitch.step}${pitch.octave}`;
      const standing = inForce.get(slot) ?? keyAlter(fifths, pitch.step);
      // Whatever the note sounds is what the next note on that line is read against, even
      // where the document hid the accidental or asked for one of its own.
      inForce.set(slot, pitch.alter);
      if (pitch.tieStop || pitch.alter === standing) continue;
      row[head.index] = accidentalName(pitch.alter);
    }
  }

  return plan;
}
